import {
  PhFile,
  PhFileArchive,
  PhFileAudio,
  PhFileC,
  PhFileCSharp,
  PhFileCode,
  PhFileCpp,
  PhFileCss,
  PhFileCsv,
  PhFileDoc,
  PhFileHtml,
  PhFileImage,
  PhFileIni,
  PhFileJpg,
  PhFileJs,
  PhFileJsx,
  PhFileMd,
  PhFilePdf,
  PhFilePng,
  PhFilePpt,
  PhFilePy,
  PhFileRs,
  PhFileSql,
  PhFileSvg,
  PhFileText,
  PhFileTs,
  PhFileTsx,
  PhFileTxt,
  PhFileVideo,
  PhFileVue,
  PhFileXls,
  PhFileZip,
  PhFolder,
} from '@phosphor-icons/vue';
import type { Component } from 'vue';
import i18n from '@/i18n';
import { isFolderLike, type FileSystemItem } from '@/components/document/sub-components/file-manager/types';

const { t } = i18n.global;

export interface FileIcon {
  icon: Component;
  color: string;
}

const FILE_ICON_BY_EXTENSION: Record<string, FileIcon> = {
  pdf: { icon: PhFilePdf, color: '#C0392B' },
  doc: { icon: PhFileDoc, color: '#2B579A' },
  docx: { icon: PhFileDoc, color: '#2B579A' },
  xls: { icon: PhFileXls, color: '#217346' },
  xlsx: { icon: PhFileXls, color: '#217346' },
  csv: { icon: PhFileCsv, color: '#217346' },
  ppt: { icon: PhFilePpt, color: '#D24726' },
  pptx: { icon: PhFilePpt, color: '#D24726' },
  txt: { icon: PhFileTxt, color: '#6B7280' },
  log: { icon: PhFileText, color: '#6B7280' },
  md: { icon: PhFileMd, color: '#6B7280' },
  ini: { icon: PhFileIni, color: '#6B7280' },
  html: { icon: PhFileHtml, color: '#E34C26' },
  htm: { icon: PhFileHtml, color: '#E34C26' },
  css: { icon: PhFileCss, color: '#264DE4' },
  js: { icon: PhFileJs, color: '#F0DB4F' },
  jsx: { icon: PhFileJsx, color: '#F0DB4F' },
  ts: { icon: PhFileTs, color: '#3178C6' },
  tsx: { icon: PhFileTsx, color: '#3178C6' },
  vue: { icon: PhFileVue, color: '#42B883' },
  json: { icon: PhFileCode, color: '#6B7280' },
  py: { icon: PhFilePy, color: '#3776AB' },
  rs: { icon: PhFileRs, color: '#CE422B' },
  c: { icon: PhFileC, color: '#A8B9CC' },
  cpp: { icon: PhFileCpp, color: '#00599C' },
  cs: { icon: PhFileCSharp, color: '#178600' },
  sql: { icon: PhFileSql, color: '#F29111' },
  png: { icon: PhFilePng, color: '#9C27B0' },
  jpg: { icon: PhFileJpg, color: '#9C27B0' },
  jpeg: { icon: PhFileJpg, color: '#9C27B0' },
  svg: { icon: PhFileSvg, color: '#9C27B0' },
  gif: { icon: PhFileImage, color: '#9C27B0' },
  bmp: { icon: PhFileImage, color: '#9C27B0' },
  webp: { icon: PhFileImage, color: '#9C27B0' },
  zip: { icon: PhFileZip, color: '#D9A441' },
  rar: { icon: PhFileArchive, color: '#D9A441' },
  '7z': { icon: PhFileArchive, color: '#D9A441' },
  tar: { icon: PhFileArchive, color: '#D9A441' },
  gz: { icon: PhFileArchive, color: '#D9A441' },
  mp3: { icon: PhFileAudio, color: '#EC4899' },
  wav: { icon: PhFileAudio, color: '#EC4899' },
  flac: { icon: PhFileAudio, color: '#EC4899' },
  mp4: { icon: PhFileVideo, color: '#3B82F6' },
  mov: { icon: PhFileVideo, color: '#3B82F6' },
  avi: { icon: PhFileVideo, color: '#3B82F6' },
  mkv: { icon: PhFileVideo, color: '#3B82F6' },
};

export const DEFAULT_FILE_ICON: FileIcon = { icon: PhFile, color: '#6B7280' };

export const getFileIcon = (name: string): FileIcon => {
  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  return FILE_ICON_BY_EXTENSION[extension] ?? DEFAULT_FILE_ICON;
};

// Folders (and folder-shaped mounts) don't vary by extension the way files do — always the same
// glyph/color, resolved here so every caller (grid tiles, context-menu header, ...) shares one
// folder-vs-file branch instead of re-implementing it.
export const getItemIcon = (item: FileSystemItem): FileIcon =>
  isFolderLike(item)
    ? { icon: PhFolder, color: 'var(--primary-color)' }
    : getFileIcon(item.name);

const OFFICE_APP_LABEL_BY_EXTENSION: Record<string, string> = {
  doc: 'word',
  docx: 'word',
  xls: 'excel',
  xlsx: 'excel',
  csv: 'excel',
  ppt: 'powerPoint',
  pptx: 'powerPoint',
  pdf: 'pdf',
};

// Names the actual editor SharePoint opens a file in ("Open in Word"/"Open in PDF"/...), falling
// back to the generic label for extensions with no dedicated Office/PDF viewer. Deliberately not
// extended to Office-family types this codebase has no other awareness of yet (Visio, OneNote) —
// see ITEM_CONTEXT_MENU_PLAN.md's Revision section.
export const getOfficeAppLabel = (name: string): string => {
  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  const appKey = OFFICE_APP_LABEL_BY_EXTENSION[extension];
  if (appKey) {
    const appName = t(
      'fileManager.fileExplorerView.renderModes.fileObject.fileItem.getFileIcon.appNames.' +
        appKey,
    );
    return t(
      'fileManager.fileExplorerView.renderModes.fileObject.fileItem.getFileIcon.openInApp',
      { app: appName },
    );
  }
  return t(
    'fileManager.fileExplorerView.renderModes.fileObject.fileItem.getFileIcon.openInSharePoint',
  );
};
